/**
 * Local encrypted key vault.
 *
 * Security model (v1):
 * - The XRPL family seed NEVER leaves this device and is never sent anywhere.
 * - At rest it is stored only as AES-256-GCM ciphertext in localStorage.
 * - The AES key is derived either from a passkey (WebAuthn PRF extension) or,
 *   when PRF is unavailable, from a passphrase via PBKDF2-SHA256 (600k iters).
 * - The plaintext seed lives in memory only while the wallet is unlocked and is
 *   zeroed on lock / auto-lock.
 *
 * All functions here touch browser-only APIs and must be called from effects,
 * event handlers or other client-only code.
 */

const VAULT_KEY = "vaultkey.vault.v1";
const SETTINGS_KEY = "vaultkey.settings.v1";

export type VaultKind = "passkey" | "passphrase";

export interface VaultRecord {
  v: 1;
  kind: VaultKind;
  /** base64url credential id, present for passkey vaults */
  credentialId?: string;
  /** base64 salt used for PRF eval / PBKDF2 */
  salt: string;
  iv: string;
  ct: string;
  address: string;
  createdAt: number;
  /** true once the user confirmed they wrote down the recovery phrase */
  backedUp: boolean;
  /**
   * Optional second envelope holding the same seed, sealed with a key derived
   * from the device passkey (WebAuthn PRF). Lets biometrics unlock a vault that
   * was created with a passcode, without ever storing the passcode.
   */
  bio?: BioEnvelope;
}

export interface BioEnvelope {
  credentialId: string;
  salt: string;
  iv: string;
  ct: string;
  addedAt: number;
}

export interface VaultSettings {
  /** auto-lock idle timeout in seconds */
  autoLockSeconds: number;
  /** allow outbound price lookups (Coinbase spot). Off = zero network calls beyond XRPL. */
  fiatLookup: boolean;
  /** require a second confirmation for amounts above this many XRP */
  largeAmountThreshold: number;
  /** require a sustained hold to sign instead of a single tap */
  holdToSign: boolean;
  /** force an explicit checksum confirmation of the destination before signing */
  confirmChecksum: boolean;
  /** blur balances until tapped (shoulder-surfing protection) */
  hideBalances: boolean;
  /** require a device biometric / passkey check before every signature */
  biometricSigning: boolean;
}

export const defaultSettings: VaultSettings = {
  autoLockSeconds: 120,
  fiatLookup: true,
  largeAmountThreshold: 1000,
  holdToSign: true,
  confirmChecksum: true,
  hideBalances: false,
  biometricSigning: false,
};

/* ---------------------------------------------------------------- encoding */

const enc = new TextEncoder();
const dec = new TextDecoder();

function toB64(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function fromB64(s: string): Uint8Array {
  const raw = atob(s);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function toB64Url(buf: ArrayBuffer | Uint8Array): string {
  return toB64(buf).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromB64Url(s: string): Uint8Array {
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  return fromB64(s.replace(/-/g, "+").replace(/_/g, "/") + pad);
}

function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n);
  crypto.getRandomValues(b);
  return b;
}

/* ------------------------------------------------------------- persistence */

export function readVault(): VaultRecord | null {
  try {
    const raw = localStorage.getItem(VAULT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as VaultRecord;
    return parsed?.v === 1 ? parsed : null;
  } catch {
    return null;
  }
}

function writeVault(record: VaultRecord) {
  localStorage.setItem(VAULT_KEY, JSON.stringify(record));
}

export function destroyVault() {
  localStorage.removeItem(VAULT_KEY);
}

export function markBackedUp() {
  const v = readVault();
  if (v) writeVault({ ...v, backedUp: true });
}

export function readSettings(): VaultSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return defaultSettings;
    return { ...defaultSettings, ...(JSON.parse(raw) as Partial<VaultSettings>) };
  } catch {
    return defaultSettings;
  }
}

export function writeSettings(next: VaultSettings) {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
}

/* ------------------------------------------------------------ key material */

async function aesKeyFromRaw(raw: ArrayBuffer | Uint8Array, salt: Uint8Array): Promise<CryptoKey> {
  const material = raw instanceof Uint8Array ? new Uint8Array(raw) : new Uint8Array(raw);
  const hkdfKey = await crypto.subtle.importKey("raw", material as BufferSource, "HKDF", false, [
    "deriveKey",
  ]);
  return crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: salt as BufferSource, info: enc.encode("vaultkey/seed") },
    hkdfKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

async function aesKeyFromPassphrase(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey("raw", enc.encode(passphrase), "PBKDF2", false, [
    "deriveKey",
  ]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", hash: "SHA-256", salt: salt as BufferSource, iterations: 600_000 },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/* ---------------------------------------------------------------- passkeys */

export function passkeysAvailable(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined" &&
    !!navigator.credentials
  );
}

interface PrfResults {
  prf?: { enabled?: boolean; results?: { first?: ArrayBuffer } };
}

/** Creates a passkey with the PRF extension. Returns null when PRF is not supported. */
async function createPasskey(
  label: string,
  salt: Uint8Array,
): Promise<{ credentialId: string; prf: ArrayBuffer } | null> {
  const userId = randomBytes(16);
  const cred = (await navigator.credentials.create({
    publicKey: {
      challenge: randomBytes(32) as BufferSource,
      rp: { name: "Cobalt Wallet", id: window.location.hostname },
      user: { id: userId as BufferSource, name: label, displayName: label },
      pubKeyCredParams: [
        { type: "public-key", alg: -7 },
        { type: "public-key", alg: -257 },
      ],
      authenticatorSelection: {
        residentKey: "required",
        userVerification: "required",
      },
      timeout: 120_000,
      attestation: "none",
      extensions: { prf: { eval: { first: salt } } } as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;

  if (!cred) return null;
  const ext = cred.getClientExtensionResults() as PrfResults;
  const credentialId = toB64Url(cred.rawId);

  if (ext.prf?.results?.first) {
    return { credentialId, prf: ext.prf.results.first };
  }
  if (ext.prf?.enabled) {
    // PRF supported but not evaluated at creation: evaluate now with an assertion.
    const prf = await evaluatePrf(credentialId, salt);
    if (prf) return { credentialId, prf };
  }
  return null;
}

let pendingBiometric: AbortController | null = null;

/** Cancels any biometric prompt still waiting, so the passcode path is never blocked. */
export function cancelBiometricPrompt() {
  pendingBiometric?.abort();
  pendingBiometric = null;
}

async function evaluatePrf(credentialId: string, salt: Uint8Array): Promise<ArrayBuffer | null> {
  cancelBiometricPrompt();
  const controller = new AbortController();
  pendingBiometric = controller;
  let assertion: PublicKeyCredential | null;
  try {
    assertion = (await navigator.credentials.get({
      signal: controller.signal,
      publicKey: {
        challenge: randomBytes(32) as BufferSource,
        allowCredentials: [{ type: "public-key", id: fromB64Url(credentialId) as BufferSource }],
        userVerification: "required",
        timeout: 60_000,
        extensions: { prf: { eval: { first: salt } } } as AuthenticationExtensionsClientInputs,
      },
    })) as PublicKeyCredential | null;
  } catch (err) {
    const name = err instanceof Error ? err.name : "";
    if (name === "AbortError") throw new VaultError("Biometric prompt cancelled.");
    if (name === "NotAllowedError")
      throw new VaultError("Biometric check was dismissed or timed out. Try again or use your passcode.");
    if (name === "SecurityError" || name === "InvalidStateError")
      throw new VaultError(
        "Biometrics were set up on a different web address. Unlock with your passcode, then re-enable biometrics in Settings.",
      );
    throw new VaultError("Biometric unlock isn't available here. Use your passcode.");
  } finally {
    if (pendingBiometric === controller) pendingBiometric = null;
  }
  if (!assertion) return null;
  const ext = assertion.getClientExtensionResults() as PrfResults;
  return ext.prf?.results?.first ?? null;
}

/* ------------------------------------------------------------------ public */

export class VaultError extends Error {}

/** Encrypts a seed under a newly created passkey. Throws if PRF is unsupported. */
export async function createPasskeyVault(
  seed: string,
  address: string,
  label = "Cobalt Wallet account",
): Promise<VaultRecord> {
  if (!passkeysAvailable()) throw new VaultError("Passkeys are not available in this browser.");
  const salt = randomBytes(32);
  const created = await createPasskey(label, salt);
  if (!created) {
    throw new VaultError(
      "This device's passkey provider does not support hardware-derived encryption keys (WebAuthn PRF). Use a passphrase-protected vault instead.",
    );
  }
  const key = await aesKeyFromRaw(created.prf, salt);
  const record = await sealSeed(seed, address, key, salt, "passkey", created.credentialId);
  writeVault(record);
  return record;
}

export async function createPassphraseVault(
  seed: string,
  address: string,
  passphrase: string,
): Promise<VaultRecord> {
  assertPassphraseStrength(passphrase);
  const salt = randomBytes(32);
  const key = await aesKeyFromPassphrase(passphrase, salt);
  const record = await sealSeed(seed, address, key, salt, "passphrase");
  writeVault(record);
  return record;
}

async function sealSeed(
  seed: string,
  address: string,
  key: CryptoKey,
  salt: Uint8Array,
  kind: VaultKind,
  credentialId?: string,
): Promise<VaultRecord> {
  const iv = randomBytes(12);
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv as BufferSource },
    key,
    enc.encode(seed),
  );
  return {
    v: 1,
    kind,
    salt: toB64(salt),
    iv: toB64(iv),
    ct: toB64(ct),
    address,
    createdAt: Date.now(),
    backedUp: false,
    ...(credentialId ? { credentialId } : {}),
  };
}

/** True when this vault can be opened with device biometrics / passkey. */
export function biometricUnlockReady(record: VaultRecord | null): boolean {
  if (!record) return false;
  return record.kind === "passkey" || Boolean(record.bio);
}

/** The passkey credential on this device that can authorize a signature. */
export function signingCredentialId(record: VaultRecord | null): string | null {
  if (!record) return null;
  return record.bio?.credentialId ?? record.credentialId ?? null;
}

/**
 * Requires a fresh device biometric (Face ID / Touch ID / Windows Hello)
 * assertion before a transaction is signed. The seed never leaves this device
 * either way — this is a local user-presence gate on the signing action, so a
 * borrowed unlocked phone cannot push a payment through.
 */
export async function assertBiometricApproval(): Promise<void> {
  if (!passkeysAvailable()) {
    throw new VaultError("This device cannot verify biometrics. Turn off biometric signing in Settings.");
  }
  const credentialId = signingCredentialId(readVault());
  if (!credentialId) {
    throw new VaultError("Enrol biometric unlock in Settings before requiring biometric signing.");
  }
  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge: randomBytes(32) as BufferSource,
      allowCredentials: [{ type: "public-key", id: fromB64Url(credentialId) as BufferSource }],
      userVerification: "required",
      timeout: 120_000,
    },
  })) as PublicKeyCredential | null;
  if (!assertion) throw new VaultError("Biometric approval was not completed. Nothing was signed.");
}

/**
 * Seals a second copy of the seed under a freshly enrolled device passkey so
 * biometrics can replace the passcode on this device. The passcode envelope is
 * left untouched as a recovery path.
 */
export async function enableBiometricUnlock(seed: string): Promise<VaultRecord> {
  const existing = readVault();
  if (!existing) throw new VaultError("No vault on this device.");
  if (!passkeysAvailable()) throw new VaultError("This browser does not support passkeys.");
  const salt = randomBytes(32);
  const created = await createPasskey("Cobalt Wallet biometric unlock", salt);
  if (!created) {
    throw new VaultError(
      "This device's biometric provider does not support hardware-derived encryption keys (WebAuthn PRF). Keep using your passcode.",
    );
  }
  const key = await aesKeyFromRaw(created.prf, salt);
  const iv = randomBytes(12);
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv as BufferSource },
    key,
    enc.encode(seed),
  );
  const next: VaultRecord = {
    ...existing,
    bio: {
      credentialId: created.credentialId,
      salt: toB64(salt),
      iv: toB64(iv),
      ct: toB64(ct),
      addedAt: Date.now(),
    },
  };
  writeVault(next);
  return next;
}

/** Removes the biometric envelope. The passcode envelope still opens the vault. */
export function disableBiometricUnlock(): VaultRecord {
  const existing = readVault();
  if (!existing) throw new VaultError("No vault on this device.");
  if (existing.kind === "passkey") {
    throw new VaultError(
      "This vault is protected only by your passkey. Set a passcode before removing biometrics.",
    );
  }
  const { bio: _bio, ...rest } = existing;
  const next = rest as VaultRecord;
  writeVault(next);
  return next;
}

async function decryptEnvelope(
  key: CryptoKey,
  iv: string,
  ct: string,
  message: string,
): Promise<string> {
  try {
    const pt = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: fromB64(iv) as BufferSource },
      key,
      fromB64(ct) as BufferSource,
    );
    return dec.decode(pt);
  } catch {
    throw new VaultError(message);
  }
}

/** Opens the vault with device biometrics (passkey PRF). */
export async function unlockWithBiometrics(): Promise<string> {
  const record = readVault();
  if (!record) throw new VaultError("No vault on this device.");

  if (record.bio) {
    const salt = fromB64(record.bio.salt);
    const prf = await evaluatePrf(record.bio.credentialId, salt);
    if (!prf) throw new VaultError("Biometric verification failed.");
    const key = await aesKeyFromRaw(prf, salt);
    return decryptEnvelope(key, record.bio.iv, record.bio.ct, "Biometric unlock failed.");
  }

  if (record.kind === "passkey") {
    if (!record.credentialId) throw new VaultError("Vault is missing its passkey reference.");
    const salt = fromB64(record.salt);
    const prf = await evaluatePrf(record.credentialId, salt);
    if (!prf) throw new VaultError("Biometric verification failed.");
    const key = await aesKeyFromRaw(prf, salt);
    return decryptEnvelope(key, record.iv, record.ct, "Biometric unlock failed.");
  }

  throw new VaultError("Biometric unlock is not set up on this device.");
}

/** Unlocks the vault and returns the plaintext seed. */
export async function unlockVault(passphrase?: string): Promise<string> {
  const record = readVault();
  if (!record) throw new VaultError("No vault on this device.");

  // No passcode supplied → biometrics (either a passkey vault or a passcode
  // vault with biometric unlock enrolled).
  if (!passphrase) return unlockWithBiometrics();

  if (record.kind === "passkey") return unlockWithBiometrics();

  const salt = fromB64(record.salt);
  const key = await aesKeyFromPassphrase(passphrase, salt);
  return decryptEnvelope(
    key,
    record.iv,
    record.ct,
    "Could not decrypt the vault. Wrong passcode.",
  );
}

export function assertPassphraseStrength(passphrase: string) {
  if (!/^\d{6}$/.test(passphrase)) {
    throw new VaultError("Use a 6-digit numeric passcode.");
  }
  if (/^(\d)\1{5}$/.test(passphrase) || "0123456789012345".includes(passphrase)) {
    throw new VaultError("Passcode is too easy to guess — avoid repeats and sequences.");
  }
}

/* ----------------------------------------------------------------- session */

const SESSION_KEY = "vaultkey.session.v1";

export interface SessionInfo {
  /** first time this device opened the vault */
  firstUnlockAt: number | null;
  lastUnlockAt: number | null;
  unlockCount: number;
  /** coarse device description, local only */
  device: string;
}

export function deviceLabel(): string {
  if (typeof navigator === "undefined") return "Unknown device";
  const ua = navigator.userAgent;
  const platform =
    /iPhone|iPad|iPod/u.test(ua)
      ? "iOS"
      : /Android/u.test(ua)
        ? "Android"
        : /Mac OS X/u.test(ua)
          ? "macOS"
          : /Windows/u.test(ua)
            ? "Windows"
            : /Linux/u.test(ua)
              ? "Linux"
              : "Unknown OS";
  const browser = /Edg\//u.test(ua)
    ? "Edge"
    : /OPR\//u.test(ua)
      ? "Opera"
      : /Chrome\//u.test(ua)
        ? "Chrome"
        : /Firefox\//u.test(ua)
          ? "Firefox"
          : /Safari\//u.test(ua)
            ? "Safari"
            : "Browser";
  return `${platform} · ${browser}`;
}

export function readSession(): SessionInfo {
  const fallback: SessionInfo = {
    firstUnlockAt: null,
    lastUnlockAt: null,
    unlockCount: 0,
    device: deviceLabel(),
  };
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return fallback;
    return { ...fallback, ...(JSON.parse(raw) as Partial<SessionInfo>), device: deviceLabel() };
  } catch {
    return fallback;
  }
}

export function recordUnlock(): SessionInfo {
  const prev = readSession();
  const next: SessionInfo = {
    firstUnlockAt: prev.firstUnlockAt ?? Date.now(),
    lastUnlockAt: Date.now(),
    unlockCount: prev.unlockCount + 1,
    device: deviceLabel(),
  };
  localStorage.setItem(SESSION_KEY, JSON.stringify(next));
  return next;
}

export function destroySession() {
  localStorage.removeItem(SESSION_KEY);
}

/* ------------------------------------------------------- passphrase change */

/**
 * Re-encrypts the existing seed under a new passphrase. The current passphrase
 * (or passkey) must succeed first, so this can never orphan a vault.
 */
export async function rotatePassphrase(
  current: string | undefined,
  next: string,
): Promise<VaultRecord> {
  const existing = readVault();
  if (!existing) throw new VaultError("No vault on this device.");
  const seed = await unlockVault(current);
  assertPassphraseStrength(next);
  if (current && current === next) {
    throw new VaultError("Choose a passphrase you have not used here before.");
  }
  const record = await createPassphraseVault(seed, existing.address, next);
  const merged: VaultRecord = {
    ...record,
    backedUp: existing.backedUp,
    createdAt: existing.createdAt,
    // keep biometric unlock working after a passcode change
    ...(existing.bio ? { bio: existing.bio } : {}),
  };
  writeVault(merged);
  return merged;
}
